import { COLORS } from '@/config/colors';
import { PROJECTILE_LOOK as L } from '@/config/projectile-look';
import { css, circle } from './draw-utils';
import type { ProceduralDef } from '../types';

/*
 * Danger language for ENEMY projectiles (docs/04 "Tehlike ↔ ganimet dili"):
 * hot red/orange rim, dark or hollow core, spikes/fins, a pulsing warning
 * glow. Never a faceted gem, never a white sparkle, never gold/blue/green —
 * those belong to pickups (loot) and the player palette.
 */

type Ctx = CanvasRenderingContext2D;

/** 0xRRGGBB + alpha → css rgba() */
export function rgba(color: number, a: number): string {
  return `rgba(${(color >> 16) & 0xff}, ${(color >> 8) & 0xff}, ${color & 0xff}, ${a})`;
}

/** Warm warning glow; `k` = pulse strength of the frame (docs/04). */
export function dangerHalo(ctx: Ctx, c: number, rIn: number, rOut: number, color: number, k: number): void {
  const halo = ctx.createRadialGradient(c, c, rIn, c, c, rOut);
  halo.addColorStop(0, rgba(color, Math.min(0.9, 0.5 * k)));
  halo.addColorStop(1, rgba(color, 0));
  circle(ctx, c, c, rOut);
  ctx.fillStyle = halo;
  ctx.fill();
}

/** Ring of triangular spikes (teeth) between rIn and rOut. */
export function spikes(
  ctx: Ctx, cx: number, cy: number, rIn: number, rOut: number,
  count: number, phase: number, halfWidth: number, color: number,
): void {
  ctx.fillStyle = css(color);
  for (let i = 0; i < count; i++) {
    const a = phase + (Math.PI * 2 * i) / count;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a - halfWidth) * rIn, cy + Math.sin(a - halfWidth) * rIn);
    ctx.lineTo(cx + Math.cos(a) * rOut, cy + Math.sin(a) * rOut);
    ctx.lineTo(cx + Math.cos(a + halfWidth) * rIn, cy + Math.sin(a + halfWidth) * rIn);
    ctx.closePath();
    ctx.fill();
  }
}

/** Dark (hollow-reading) core with a small hot ember off-centre. */
export function darkCore(ctx: Ctx, cx: number, cy: number, r: number, ember: number): void {
  circle(ctx, cx, cy, r);
  ctx.fillStyle = css(COLORS.bulletCoreDark);
  ctx.fill();
  circle(ctx, cx + r * 0.22, cy - r * 0.28, r * 0.26);
  ctx.fillStyle = rgba(ember, 0.85);
  ctx.fill();
}

/**
 * A pulsing enemy texture: one frame per PROJECTILE_LOOK.pulse entry, the
 * draw call gets that frame's glow strength.
 */
export function hostile(
  key: string, width: number, height: number,
  draw: (ctx: Ctx, w: number, h: number, k: number, frame: number) => void,
): ProceduralDef {
  return {
    key, width, height, anchorX: 0.5, anchorY: 0.5,
    frames: L.pulse.length,
    look: 'pulse',
    draw(ctx, w, h, frame) {
      draw(ctx, w, h, L.pulse[frame] ?? 1, frame);
    },
  };
}

/**
 * A fused enemy texture (bomb / egg / splitting bolt): `fuseSteps` × 2
 * frames. `body` draws at the centre with radius `r`; this adds the fuse
 * spark and the danger ring that grows to the blast hint (docs/04).
 */
export function fused(
  key: string, size: number,
  body: (ctx: Ctx, c: number, r: number, progress: number) => void,
  spark: { dx: number; dy: number } | null,
): ProceduralDef {
  return {
    key, width: size, height: size, anchorX: 0.5, anchorY: 0.5,
    frames: L.fuseSteps * 2,
    look: 'fuse',
    draw(ctx, w, _h, frame) {
      const c = w / 2;
      const r = c * L.fuseBodyFrac;
      const step = Math.floor(frame / 2);
      const progress = step / (L.fuseSteps - 1);
      const flick = frame % 2 === 1;
      dangerRing(ctx, c, r, c * L.fuseRingMax, progress, flick);
      dangerHalo(ctx, c, r * 0.6, r * 1.7, COLORS.dangerRed, 0.8 + progress * 0.6);
      body(ctx, c, r, progress);
      if (spark) fuseSpark(ctx, c + spark.dx * r, c + spark.dy * r, r * 0.55, flick, progress);
    },
  };
}

/**
 * The bomb read: a dashed red ring that swells from the body to the blast
 * hint as the fuse runs down; the last step closes it solid and doubles it.
 */
function dangerRing(ctx: Ctx, c: number, r: number, max: number, p: number, flick: boolean): void {
  const rr = r * 1.25 + (max - r * 1.25) * p;
  const last = p >= 1;
  ctx.save();
  ctx.lineWidth = 2 + p * 2;
  ctx.strokeStyle = rgba(COLORS.dangerRed, (0.4 + 0.55 * p) * (flick ? 1 : 0.8));
  if (!last) ctx.setLineDash([5, 4]);
  circle(ctx, c, c, rr);
  ctx.stroke();
  if (last) {
    ctx.strokeStyle = rgba(COLORS.dangerOrange, flick ? 0.9 : 0.6);
    ctx.lineWidth = 2;
    circle(ctx, c, c, rr * 0.82);
    ctx.stroke();
  }
  ctx.restore();
}

/** Ragged hot fuse spark — orange rays, never the white 4-point loot sparkle. */
function fuseSpark(ctx: Ctx, x: number, y: number, s: number, flick: boolean, p: number): void {
  const n = 7;
  const size = s * (flick ? 1.25 : 0.85) * (1 + p * 0.3);
  ctx.fillStyle = css(COLORS.enemyFuse);
  ctx.beginPath();
  for (let i = 0; i < n * 2; i++) {
    const a = (Math.PI * i) / n + (flick ? 0.3 : 0);
    const rr = i % 2 === 0 ? size : size * 0.4;
    if (i === 0) ctx.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    else ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fill();
  circle(ctx, x, y, size * 0.32);
  ctx.fillStyle = css(COLORS.enemyFuseCore);
  ctx.fill();
}
